import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j218Api, FJ730, FJ731 } from '../../../services/j218Api';
import { Mode } from './useJ218Mode';

export function useJ218Record() {
  const [record, setRecord] = useState<FJ730 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ730>>({});
  const [details, setDetails] = useState<Partial<FJ731>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string): Promise<{
    data: FJ730;
    dets: Partial<FJ731>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await j218Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ730, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ730): unknown => {
    return (formData as Record<string, unknown>)[key] ?? '';
  }, [formData]);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ730) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FJ730,
        details: details as FJ731[],
      };
      if (mode === 'add') {
        const result = await j218Api.create(req);
        message.success(`新增成功: ${result.fj730_01}`);
        onSuccess(result);
      } else {
        const result = await j218Api.update(formData.fj730_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { errors?: Array<{ field: string; message: string }> } } };
      if (error?.response?.status === 422 && error.response.data?.errors) {
        const msgs = error.response.data.errors.map(
          (e: { field: string; message: string }) => e.message
        );
        message.error(msgs.join('; '));
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record) return;
    setLoading(true);
    try {
      await j218Api.delete(record.fj730_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  const addDetailRow = useCallback(() => {
    const newRow: Partial<FJ731> = {
      fj731_02: details.length + 1,
      fj731_11: '',
      fj731_12: '',
    };
    setDetails(prev => [...prev, newRow]);
  }, [details]);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== idx);
      // T-F05: reindex 1-based
      return next.map((row, i) => ({ ...row, fj731_02: i + 1 }));
    });
  }, []);

  const revertToRecord = useCallback(() => {
    if (!record) return;
    setFormData({ ...record });
    setDetails(record.details || []);
  }, [record]);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fj730_80: 'N' });
    setDetails([]);
    setSelectedRow(0);
  }, []);

  const applyResult = useCallback((result: FJ730) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  const applyDraft = useCallback((draft: FJ730) => {
    setRecord(null);
    setFormData({ ...draft });
    setDetails(draft.details || []);
    setSelectedRow(0);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, applyDraft,
  };
}
